import { Component } from "@angular/core";
import { ConfirmationService, MenuItem } from "primeng/api";
import { SubSink } from "subsink";
import { IEmployeeTrainingRecord } from "../shared/models/employee-training-record";
import { BusinessOperation } from "../shared/models/business-operation";
import { DialogService } from "primeng/dynamicdialog";
import { NgxSpinnerService } from "ngx-spinner";
import { ToastService } from "../shared/toast/toast.service";
import { AuthenticationService } from "../services/authentication.service";
import { Router } from "@angular/router";
import { PpsUtilityService } from "../services/pps-utility.service";
import { BreakpointObserver, BreakpointState } from "@angular/cdk/layout";
import { EmployeeTrainingRecordService } from "../services/employee-training-record.service";
import { EmployeeBase } from "../shared/models/employee-base";
import { ITrainingCourse } from "../shared/models/training-course";
import { EmployeeTrainingRecordFormComponent } from "./employee-training-record-form.component";
import { DeleteActionComponent } from "../system-settings/delete-action.component";

@Component({
  selector: "pps-employee-training-record",
  templateUrl: "./employee-training-record.component.html"
})
export class EmployeeTrainingRecordComponent{
  private subs = new SubSink();

  smallScreen = false;

  items: MenuItem[] = [{label: "Employee Training Record", icon: "pi-fw fas fa-user-graduate"}];
  spinnerName: string = "employeeSpinner";

  context:any = {};
  employeeTrainingRecords: IEmployeeTrainingRecord[] = [];
  employees: EmployeeBase[] = [];
  trainingCourses: ITrainingCourse[] = [];

  gridHeaders = [];
  gridOptions = {
    getRowId: param => `${param.data.id}`,
    defaultColDef: {
      resizable: true,
      floatingFilter: true
    }
  };

  selectedRow: any = null;
  selectedRowNode: any = null; //ag-grid RowNode

  private gridApi;

  constructor(private _dialog: DialogService,
    private _spinner: NgxSpinnerService,
    private _toast: ToastService,
    private _employeeTrainingRecordService: EmployeeTrainingRecordService,
    private _authService: AuthenticationService,
    private _confirm: ConfirmationService,
    private _util: PpsUtilityService,
    private breakpointObserver: BreakpointObserver) { 
      this.subs.sink = this.breakpointObserver.observe(['(min-width: 768px)'])
      .subscribe((state: BreakpointState) => {
        this.smallScreen = !state.matches;
      });      
  }

  ngOnDestroy(): void {
    this.subs.unsubscribe();
  }

  ngOnInit(): void {
    this.initialize();
  }

  private initialize(){
    this.context = { componentParent: this };
    this.gridHeaders = this.getColumnDefs();
    this.loadEntities();
  }

  onGridReady(params) {
    this.gridApi = params.api;
  }

  onRowClicked(evt){
    let _this = evt.context.componentParent;
    var selectedRows = _this.gridApi.getSelectedRows();
    _this.selectedRow = selectedRows.length === 1 ? selectedRows[0] : null;
    _this.selectedRowNode = evt.node;
  }

  onRowDblClicked(params){
    if (this.canManageEmployeeTrainingRecord()){
      let employeeTrainingRecord = params.data;
      this.openEmployeeTrainingRecordForm(employeeTrainingRecord);
    }
  }

  loadEmployeeTrainingRecords(){
    this._spinner.show(this.spinnerName);
    this.employeeTrainingRecords = [];
    this.subs.sink = this._employeeTrainingRecordService
      .getAll()
      .subscribe(response => {
        if (response.succeed){
          this.employeeTrainingRecords = response.result;
          this._toast.succeeded(`${this.employeeTrainingRecords.length} records have been loaded`);
        }else{
          this._toast.failed("Loading employee training records failed: " + response.errors.join(";"));
        }
      })
      .add(() => this._spinner.hide(this.spinnerName));
  }

  openEmployeeTrainingRecordForm(employeeTrainingRecord: IEmployeeTrainingRecord){
    if (employeeTrainingRecord === null){
      employeeTrainingRecord = {
        id: null,
        employeeId: null,
        courseId: null,
        enrollmentDate: new Date(),
      }
    }

    let dialog = this._dialog.open(EmployeeTrainingRecordFormComponent, {
      header: employeeTrainingRecord.id ? "Update Training Record" : "Add Training Record", 
      styleClass: "size-md",
      data:{
        employees: this.employees,
        courses: this.trainingCourses,
        employeeTrainingRecord: employeeTrainingRecord
      },
      draggable: true
    });


    this.subs.sink = dialog.onClose.subscribe(employeeTrainingRecord => {
      if (employeeTrainingRecord){
        this.save(employeeTrainingRecord);
      }
    });
  }

  save(employeeTrainingRecord: IEmployeeTrainingRecord){
    if (!employeeTrainingRecord.id){  // add new
      employeeTrainingRecord.id = 0;
      this.subs.sink = this._employeeTrainingRecordService
        .add(employeeTrainingRecord)
        .subscribe(response => {
          if (response.succeed){
            this.employeeTrainingRecords.unshift(response.result);
            this._toast.succeeded("Employee training record saved successfully");
            this.gridApi.setGridOption('rowData', this.employeeTrainingRecords);
          }else{
            this._toast.failed("Failed to save employee training record: " + response.errors.join(";"));
          }
        });
    }else{
      this.subs.sink = this._employeeTrainingRecordService
        .update(employeeTrainingRecord)
        .subscribe(response => {
          if (response.succeed){
            var foundIndex = this.employeeTrainingRecords.findIndex(x => x.id == response.result.id);
            this.employeeTrainingRecords[foundIndex] = response.result;
            this.resetGrid(this.employeeTrainingRecords, employeeTrainingRecord.id, false);
            this._toast.succeeded("Employee training record updated successfully");
          }else{
            this._toast.failed("Failed to update employee training record: " + response.errors.join(";"));
          }
        });
    }
  }

  getFilteredCount(){
    if (this.gridApi){
      return this.gridApi.getModel().rootNode.childrenAfterFilter.length;
    }

    return 0;
  }

  canManageEmployeeTrainingRecord(): boolean{
    return this._authService.canUserDoOperartion(BusinessOperation.ManageEmployeeTrainingRecord);
  }

  delete(item: IEmployeeTrainingRecord){
    if (item === null){
      return;
    }

    this._confirm.confirm({
      header: `Delete Employee Training Record`,
      message: `Are you sure that you want to delete this training record?`,
      icon: "fas fa-exclamation-circle icon-danger",
      defaultFocus: "accept",
      accept: () => this.deleteHelper(item)
    });
  }

  deleteHelper(item: IEmployeeTrainingRecord){
    this.subs.sink = this._employeeTrainingRecordService.delete(item.id)
      .subscribe(apiResponse => {
        if (apiResponse.succeed){
          var foundIndex = this.employeeTrainingRecords.findIndex(x => x.id == item.id);
          if (foundIndex !== -1 ){
            this.employeeTrainingRecords.splice(foundIndex, 1);            
            this.gridApi.setGridOption('rowData', this.employeeTrainingRecords);
            this._toast.succeeded(`The employee training record deleted!`);
          }else{
            this._toast.failed(`Delete the employee's training record failed!`);
          }
        }else{
          this._toast.failed(`Failed to delete employee training record: ${apiResponse.errors.join("|")}`);
        }
      });
  }  

  private getActionComponentTooltip(employeeTrainingRecord: IEmployeeTrainingRecord):string{
    return "Click to delete employee training record";
  }

  private getColumnDefs(){
    let colDefs = [
      {
        headerName: "",
        field: "action",
        cellRenderer: DeleteActionComponent,
        resizable: false,
        width: 50,
        hide: false,
        filter: false,
        pinned: true,
        suppressMovable: true,
        cellStyle: {textAlign: "center"}
      },      
      {
        headerName: 'Full Name',
        field:'fullname',
        filter: true,
        sortable: true,
        width: 350,
        suppressMovable: true,
        valueGetter: this.getEmployeeFullname
      },
      {
        headerName: 'Course Title',
        field:'courseTitle',
        filter: true,
        sortable: true,
        flex: 1
      },
      {
        headerName: 'EnrollmentDate',
        field:'enrollmentDate',
        filter: true,
        sortable: true,
        width: 300,
        valueGetter: this.getDateValue
      },
      {
        headerName: 'Completion Date',
        field: 'completionDate',
        filter: true,
        sortable: true,
        width: 150,
        valueGetter: this.getDateValue
      },
      {
        headerName: 'Cost',
        field:'cost',
        filter: true,
        sortable: true,
        width: 200,
        cellStyle: this._util.getNumericCellStyle({borderRightColor:'#eeeeee'}),
        valueGetter: params => params.context.componentParent._util.formatCurrency(params.data[params.colDef.field])
      }
    ];

    return colDefs;
  }

  private getEmployeeFullname(params){
    let employee: EmployeeBase = {
      firstName: params.data.employeeFirstName,
      lastName: params.data.employeeLastName
    }
    return params.context.componentParent._util.getEmployeeName(employee);
  }

  //
  // This is to update the values of the project grid and make sure the selected
  // row is rendered correctly
  private resetGrid(gridData: any, selectedId: number, ensureVisible: boolean){
    this.gridApi.setGridOption('rowData', gridData);
    
    if (selectedId !== 0 && ensureVisible){
      var node = this.gridApi.getRowNode(selectedId);
      if (node){
        this.gridApi.ensureIndexVisible(node.rowIndex, 'middle');
        node.setSelected(true);
      }
    }
  }

  private getDateValue(params){
    if (params.data[params.colDef.field]){
      let value = params.context.componentParent._util.getDateString(params.data[params.colDef.field]);
      return value;
    }

    return "";
  }

  private loadEntities(){
    this.subs.sink = this._employeeTrainingRecordService.getEmployeeTrainingRecordEntities()
      .subscribe(res => {
        if (res.succeed){
          this.employees = res.result.employees;
          this.trainingCourses = res.result.trainingCourses;
          this.loadEmployeeTrainingRecords();
        }
      });
  }
}